"use client";

import { c as _c } from "react/compiler-runtime";
const DEFAULT_SHARE_AXIS_MAX = 100;
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useContainerSize } from "@/hooks/useContainerSize";
import { AxisLeft, AxisRight } from "@visx/axis";
import { curveMonotoneX } from "@visx/curve";
import { localPoint } from "@visx/event";
import { scaleLinear } from "@visx/scale";
import { AreaClosed, LinePath } from "@visx/shape";
import { formatUtcDate, formatUtcHourlyBucket, parseCalendarDate, parseUtcHourlyBucket } from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import type { HourlyStats, StreamStatsProgressResponse, StreamStatsSummaryResponse, TargetSource } from "@/features/stream-stats/api/stream-stats-queries";
import { splitAirtimeMinutes } from "../../airtime-format";
import { computeAirtimeScaleMaxHours, resolveAirtimeReferenceHours } from "../../airtime-scale";
import { GraphContextMessage } from "../graph-context-message/graph-context-message";
import { TARGET_CARD_MESSAGES } from "../target-cards/messages";
import styles from "./overview-progress-chart.module.scss";
type ProgressPoint = {
  /** 1-based index for x-axis positioning. */
  day: number;
  /** Raw date string from the API. */
  date: string;
  /** Hour of day (0–23) when rendering hourly stats. */
  hour?: number;
  accumulatedAirtime: number;
  sharePct: number;
  /** Pre-formatted cumulative airtime for tooltip (avoids NaN from raw hours). */
  tooltipAirtime?: string;
  /** False for padded x-axis slots without API data. */
  hasData?: boolean;
};
const MIN_PROGRESS_DAYS = 7;
const GRAPH_HEIGHT = 220;
const LEFT_AXIS_W = 16;
const RIGHT_AXIS_W = 12;
/** Matches `--typography-size-xs` / `--typography-line-height-xs` for SVG layout math. */
const AXIS_LABEL_LINE_HEIGHT = 16;
/** Each x-axis label row uses `--typography-line-height-xs` (16px). */
const X_AXIS_LABEL_ROW_HEIGHT = AXIS_LABEL_LINE_HEIGHT;
const DAY_LABELS_X_PADDING = 14;
/** Labels sit inside the plot next to the axis lines (Figma). */
const Y_AXIS_LABEL_INSET = 4;
const TOOLTIP_WIDTH = 144;
const TOOLTIP_HEIGHT = 135;
const TOOLTIP_OFFSET = 18;
const HOURLY_AXIS_MAX = 23;
/** Figma 413:19388 — five ticks spread evenly; text is 1AM … 12PM (not clock positions). */
const HOURLY_AXIS_LABELS = [1, 6, 0, 18, 12] as const;
function formatHourWithPeriod(hour: number): string {
  const h12 = hour % 12 || 12;
  const period = hour < 12 ? "AM" : "PM";
  return `${h12}${period}`;
}
function getEdgeTextAnchor(isFirst: boolean, isLast: boolean) {
  if (isFirst) return "start";
  if (isLast) return "end";
  return "middle";
}
function progressPointXValue(point: ProgressPoint, isHourly: boolean): number {
  return isHourly && point.hour !== undefined ? point.hour : point.day;
}
function emptyHourlyPoint(hour: number, date: string): ProgressPoint {
  return {
    day: hour + 1,
    date,
    hour,
    accumulatedAirtime: 0,
    sharePct: 0,
    tooltipAirtime: "0m",
    hasData: false
  };
}
const EMPTY_DAILY_POINT: ProgressPoint = {
  day: 1,
  date: "",
  accumulatedAirtime: 0,
  sharePct: 0,
  hasData: false
};
function resolveChartSelection(isHourly: boolean, selectedDay: number | null, dayCount: number, progressData: ProgressPoint[]): {
  index: number;
  point: ProgressPoint;
} {
  const min = isHourly ? 0 : 1;
  const max = isHourly ? HOURLY_AXIS_MAX : dayCount;
  const index = Math.min(max, Math.max(min, selectedDay ?? min));
  const date = progressData[0]?.date ?? "";
  if (isHourly) {
    return {
      index,
      point: progressData.find(p => p.hour === index) ?? emptyHourlyPoint(index, date)
    };
  }
  return {
    index,
    point: progressData.find(p => p.day === index) ?? progressData[index - 1] ?? progressData[0] ?? EMPTY_DAILY_POINT
  };
}
function dayLabelX(day: number, dayCount: number, left: number, right: number): number {
  return left + (day - 1) / (dayCount - 1) * (right - left);
}
function hourlyLabelX(labelIndex: number, left: number, right: number): number {
  return dayLabelX(labelIndex, HOURLY_AXIS_LABELS.length, left, right);
}

/** Space reserved below the plot for day x-axis labels (tablet+). */
function xAxisBandHeight(isMobile: boolean) {
  if (isMobile) return 0;
  return X_AXIS_LABEL_ROW_HEIGHT;
}
function yAxisTickLabelProps(side: "left" | "right", referenceTick: number) {
  const isLeft = side === "left";
  return (value: unknown) => ({
    className: cn(isLeft ? styles.yAxisTickLeft : styles.yAxisTickRight, Number(value) === referenceTick && (isLeft ? styles.yAxisTickReference : styles.yAxisTickRightReference)),
    // Figma — tick labels inside the plot, tucked next to the axis line
    textAnchor: (isLeft ? "end" : "start") as "end" | "start",
    dx: isLeft ? -Y_AXIS_LABEL_INSET : Y_AXIS_LABEL_INSET,
    dy: 4
  });
}
function formatAirtimeMinutes(minutes: number): string {
  const {
    h,
    m
  } = splitAirtimeMinutes(minutes);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}
function formatTooltipDate(date: string, hour?: number): string {
  if (!date) return "";
  if (hour !== undefined && Number.isFinite(hour)) {
    return formatUtcHourlyBucket({
      date,
      hour
    }) ?? "";
  }
  return formatUtcDate(parseCalendarDate(date), "MMM d");
}
function parseStatDate(date: string): Date {
  return parseCalendarDate(date);
}
function isValidStatDate(date: string): boolean {
  return !Number.isNaN(parseStatDate(date).getTime());
}
function mapDailyStatsToProgressPoints(stats: {
  date: string;
  accumulatedAirtime: number;
  accShareOfAirtime?: number;
}[]): ProgressPoint[] {
  return stats.map((stat, index) => ({
    day: index + 1,
    date: stat.date,
    accumulatedAirtime: stat.accumulatedAirtime / 60,
    sharePct: Math.round(stat.accShareOfAirtime ?? 0),
    tooltipAirtime: formatAirtimeMinutes(stat.accumulatedAirtime)
  })).filter(point => isValidStatDate(point.date));
}
function mapHourlyStatsToProgressPoints(stats: HourlyStats[]): ProgressPoint[] {
  const points = stats.flatMap(stat => {
    const bucket = parseUtcHourlyBucket({
      date: stat.date,
      hour: stat.hour
    });
    if (!bucket) return [];
    return [{
      date: formatUtcDate(bucket, "yyyy-MM-dd"),
      hour: bucket.getUTCHours(),
      accumulatedAirtime: stat.accumulatedAirtime / 60,
      sharePct: Math.round(stat.accShareOfAirtime ?? 0),
      tooltipAirtime: formatAirtimeMinutes(stat.accumulatedAirtime)
    }];
  }).sort((a, b) => a.hour - b.hour);
  return points.map((point, index) => ({
    ...point,
    day: index + 1
  }));
}
function formatSharePercent(sharePct: number): string {
  return `${Math.round(sharePct)}%`;
}
function formatTooltipAirtime(point: ProgressPoint) {
  if (point.tooltipAirtime) return point.tooltipAirtime;

  // Fallback when tooltipAirtime unset; guard missing API values
  const hours = Number.isFinite(point.accumulatedAirtime) ? point.accumulatedAirtime : 0;
  const totalMin = Math.round(hours * 60);
  const {
    h: wholeHours,
    m: minutes
  } = splitAirtimeMinutes(totalMin);
  if (wholeHours === 0) return `${minutes}m`;
  if (minutes === 0) return `${wholeHours}h`;
  return `${wholeHours}h ${String(minutes).padStart(2, "0")}m`;
}
function padProgressData(points: ProgressPoint[], startDate?: string): ProgressPoint[] {
  if (points.length >= MIN_PROGRESS_DAYS) return points;
  const monthStart = startDate ? parseCalendarDate(startDate) : null;
  const padded = [...points];
  for (let day = points.length + 1; day <= MIN_PROGRESS_DAYS; day++) {
    let date = "";
    if (monthStart) {
      const d = new Date(monthStart);
      d.setUTCDate(d.getUTCDate() + day - 1);
      date = formatUtcDate(d, "yyyy-MM-dd");
    }
    padded.push({
      day,
      date,
      accumulatedAirtime: 0,
      sharePct: 0,
      hasData: false
    });
  }
  return padded;
}
function uniqueDesc(values: number[]) {
  return [...new Set(values)].sort((a, b) => b - a);
}

/** Contract reference → "Target"; personal best (or other) → "PB". */
function referenceAxisLabel(source?: TargetSource): "Target" | "PB" {
  return source === "CONTRACT" ? "Target" : "PB";
}
function ProgressChartSvg(t0) {
  const $ = _c(212);
  const {
    width,
    height: t1,
    selectedDay,
    onSelectDay,
    referenceAirtimeHours,
    airtimeReferenceTick,
    showAirtimeReferenceLine,
    airtimeReferenceLabel,
    referenceSharePct,
    shareReferenceTick,
    showShareReferenceLine,
    shareReferenceLabel,
    airtimeMax,
    shareMax,
    airtimeTicks,
    shareTicks,
    progressData,
    isHourly: t2
  } = t0;
  const graphHeight = t1 === undefined ? GRAPH_HEIGHT : t1;
  const isHourly = t2 === undefined ? false : t2;
  const isMobile = useIsMobile();
  const plotBottom = xAxisBandHeight(isMobile);
  const t3 = useId();
  let t4;
  if ($[0] !== t3) {
    let t5;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = /:/g;
      $[2] = t5;
    } else {
      t5 = $[2];
    }
    t4 = t3.replace(t5, "");
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const chartUid = t4;
  const airtimeAreaVerticalId = `airtime-area-v-${chartUid}`;
  const airtimeEdgeMaskGradId = `airtime-edge-mask-grad-${chartUid}`;
  const airtimeEdgeMaskId = `airtime-edge-mask-${chartUid}`;
  const plotWidth = Math.max(width + 10, 120);
  const plotLeft = isMobile ? 18 : LEFT_AXIS_W;
  const plotRight = Math.max(plotLeft + 24, plotWidth - (isMobile ? 16 : RIGHT_AXIS_W));
  const plotInnerWidth = plotRight - plotLeft;
  const dayLabelLeft = plotLeft + DAY_LABELS_X_PADDING;
  const dayLabelRight = plotRight - DAY_LABELS_X_PADDING;
  const plotHeight = graphHeight - plotBottom;
  const dayCount = progressData.length;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== airtimeAreaVerticalId || $[4] !== airtimeEdgeMaskGradId || $[5] !== airtimeEdgeMaskId || $[6] !== airtimeMax || $[7] !== airtimeReferenceLabel || $[8] !== airtimeReferenceTick || $[9] !== airtimeTicks || $[10] !== dayCount || $[11] !== dayLabelLeft || $[12] !== dayLabelRight || $[13] !== graphHeight || $[14] !== isHourly || $[15] !== onSelectDay || $[16] !== plotHeight || $[17] !== plotInnerWidth || $[18] !== plotLeft || $[19] !== plotRight || $[20] !== plotWidth || $[21] !== progressData || $[22] !== referenceAirtimeHours || $[23] !== referenceSharePct || $[24] !== selectedDay || $[25] !== shareMax || $[26] !== shareReferenceLabel || $[27] !== shareReferenceTick || $[28] !== shareTicks || $[29] !== showAirtimeReferenceLine || $[30] !== showShareReferenceLine) {
    const DAYS = Array.from({
      length: dayCount
    }, _temp);
    let t24;
    bb0: {
      if (isHourly) {
        let t25;
        if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
          t25 = [0, HOURLY_AXIS_MAX];
          $[50] = t25;
        } else {
          t25 = $[50];
        }
        t24 = t25;
        break bb0;
      }
      if (dayCount > 1) {
        let t25;
        if ($[51] !== dayCount) {
          t25 = [1, dayCount];
          $[51] = dayCount;
          $[52] = t25;
        } else {
          t25 = $[52];
        }
        t24 = t25;
        break bb0;
      }
      let t25;
      if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
        t25 = [0.5, 1.5];
        $[53] = t25;
      } else {
        t25 = $[53];
      }
      t24 = t25;
    }
    const xScaleDomain = t24;
    let t25;
    if ($[54] !== dayLabelLeft || $[55] !== dayLabelRight || $[56] !== xScaleDomain) {
      t25 = scaleLinear({
        domain: xScaleDomain,
        range: [dayLabelLeft, dayLabelRight]
      });
      $[54] = dayLabelLeft;
      $[55] = dayLabelRight;
      $[56] = xScaleDomain;
      $[57] = t25;
    } else {
      t25 = $[57];
    }
    const xScale = t25;
    let t26;
    if ($[58] !== airtimeMax || $[59] !== plotHeight) {
      t26 = scaleLinear({
        domain: [0, airtimeMax],
        range: [plotHeight, 0]
      });
      $[58] = airtimeMax;
      $[59] = plotHeight;
      $[60] = t26;
    } else {
      t26 = $[60];
    }
    const airtimeScale = t26;
    let t27;
    if ($[61] !== plotHeight || $[62] !== shareMax) {
      t27 = scaleLinear({
        domain: [0, shareMax],
        range: [plotHeight, 0]
      });
      $[61] = plotHeight;
      $[62] = shareMax;
      $[63] = t27;
    } else {
      t27 = $[63];
    }
    const shareScale = t27;
    const isInspecting = selectedDay !== null;
    let t28;
    if ($[64] !== progressData) {
      t28 = progressData.filter(_temp2);
      $[64] = progressData;
      $[65] = t28;
    } else {
      t28 = $[65];
    }
    const dataSeries = t28;
    const chartSeries = isHourly ? progressData : dataSeries;
    const {
      index: boundedIndex,
      point: selectedPoint
    } = resolveChartSelection(isHourly, selectedDay, dayCount, progressData);
    const showInspectionTooltip = selectedPoint.hasData !== false;
    const selectedX = xScale(isHourly ? boundedIndex : selectedPoint.day);
    const selectedAirtimeY = airtimeScale(selectedPoint.accumulatedAirtime);
    const selectedShareY = shareScale(selectedPoint.sharePct);
    const airtimeReferenceY = referenceAirtimeHours !== null ? airtimeScale(referenceAirtimeHours) : null;
    let t29;
    if ($[66] !== referenceSharePct || $[67] !== shareScale) {
      t29 = referenceSharePct !== null ? shareScale(referenceSharePct) : null;
      $[66] = referenceSharePct;
      $[67] = shareScale;
      $[68] = t29;
    } else {
      t29 = $[68];
    }
    const shareReferenceY = t29;
    const preferRightLeft = selectedX + TOOLTIP_OFFSET;
    const shouldPlaceLeft = preferRightLeft + TOOLTIP_WIDTH > plotWidth - 4;
    const preferredLeft = shouldPlaceLeft ? selectedX - TOOLTIP_WIDTH - TOOLTIP_OFFSET : preferRightLeft;
    const tooltipLeft = Math.max(0, Math.min(preferredLeft, plotWidth - TOOLTIP_WIDTH));
    const svgHeight = graphHeight;
    const preferredTop = selectedAirtimeY - 44;
    const tooltipTop = Math.max(0, Math.min(preferredTop, svgHeight - TOOLTIP_HEIGHT));
    const dayLabelY = svgHeight;
    let t30;
    if ($[69] !== dayCount || $[70] !== isHourly || $[71] !== onSelectDay || $[72] !== plotLeft || $[73] !== plotRight || $[74] !== xScale) {
      t30 = event => {
        const point_0 = localPoint(event);
        if (!point_0) {
          return;
        }
        const boundedX = Math.max(plotLeft, Math.min(plotRight, point_0.x));
        if (isHourly) {
          const hour = Math.max(0, Math.min(HOURLY_AXIS_MAX, Math.round(xScale.invert(boundedX))));
          onSelectDay(hour);
          return;
        }
        const day = Math.max(1, Math.min(dayCount, Math.round(xScale.invert(boundedX))));
        onSelectDay(day);
      };
      $[69] = dayCount;
      $[70] = isHourly;
      $[71] = onSelectDay;
      $[72] = plotLeft;
      $[73] = plotRight;
      $[74] = xScale;
      $[75] = t30;
    } else {
      t30 = $[75];
    }
    const pickDay = t30;
    t17 = plotWidth;
    t18 = svgHeight;
    t19 = `0 0 ${plotWidth} ${svgHeight}`;
    t20 = styles.chartSvg;
    t21 = "Airtime and share of airtime chart";
    if ($[76] !== onSelectDay) {
      t22 = event_0 => {
        if (event_0.pointerType === "touch") {
          return;
        }
        onSelectDay(null);
      };
      $[76] = onSelectDay;
      $[77] = t22;
    } else {
      t22 = $[77];
    }
    let t31;
    let t32;
    let t33;
    let t34;
    if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <stop offset="0%" stopColor="#FF9F0F" stopOpacity={0.52} />;
      t32 = <stop offset="35%" stopColor="#FF9F0F" stopOpacity={0.22} />;
      t33 = <stop offset="78%" stopColor="#FF9F0F" stopOpacity={0.06} />;
      t34 = <stop offset="100%" stopColor="#FF9F0F" stopOpacity={0} />;
      $[78] = t31;
      $[79] = t32;
      $[80] = t33;
      $[81] = t34;
    } else {
      t31 = $[78];
      t32 = $[79];
      t33 = $[80];
      t34 = $[81];
    }
    let t35;
    if ($[82] !== airtimeAreaVerticalId) {
      t35 = <linearGradient id={airtimeAreaVerticalId} x1="0" y1="0" x2="0" y2="1">{t31}{t32}{t33}{t34}</linearGradient>;
      $[82] = airtimeAreaVerticalId;
      $[83] = t35;
    } else {
      t35 = $[83];
    }
    let t36;
    let t37;
    let t38;
    let t39;
    let t40;
    let t41;
    let t42;
    if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <stop offset="0%" stopColor="white" stopOpacity={0} />;
      t37 = <stop offset="10%" stopColor="white" stopOpacity={0.55} />;
      t38 = <stop offset="22%" stopColor="white" stopOpacity={0.95} />;
      t39 = <stop offset="50%" stopColor="white" stopOpacity={1} />;
      t40 = <stop offset="78%" stopColor="white" stopOpacity={0.95} />;
      t41 = <stop offset="90%" stopColor="white" stopOpacity={0.55} />;
      t42 = <stop offset="100%" stopColor="white" stopOpacity={0} />;
      $[84] = t36;
      $[85] = t37;
      $[86] = t38;
      $[87] = t39;
      $[88] = t40;
      $[89] = t41;
      $[90] = t42;
    } else {
      t36 = $[84];
      t37 = $[85];
      t38 = $[86];
      t39 = $[87];
      t40 = $[88];
      t41 = $[89];
      t42 = $[90];
    }
    let t43;
    if ($[91] !== airtimeEdgeMaskGradId || $[92] !== plotWidth) {
      t43 = <linearGradient id={airtimeEdgeMaskGradId} gradientUnits="userSpaceOnUse" x1={0} y1={0} x2={plotWidth} y2={0}>{t36}{t37}{t38}{t39}{t40}{t41}{t42}</linearGradient>;
      $[91] = airtimeEdgeMaskGradId;
      $[92] = plotWidth;
      $[93] = t43;
    } else {
      t43 = $[93];
    }
    const t44 = `url(#${airtimeEdgeMaskGradId})`;
    let t45;
    if ($[94] !== plotHeight || $[95] !== plotInnerWidth || $[96] !== plotLeft || $[97] !== t44) {
      t45 = <rect x={plotLeft} y={0} width={plotInnerWidth} height={plotHeight} fill={t44} />;
      $[94] = plotHeight;
      $[95] = plotInnerWidth;
      $[96] = plotLeft;
      $[97] = t44;
      $[98] = t45;
    } else {
      t45 = $[98];
    }
    let t46;
    if ($[99] !== airtimeEdgeMaskId || $[100] !== plotHeight || $[101] !== plotWidth || $[102] !== t45) {
      t46 = <mask id={airtimeEdgeMaskId} maskUnits="userSpaceOnUse" x={0} y={0} width={plotWidth} height={plotHeight}>{t45}</mask>;
      $[99] = airtimeEdgeMaskId;
      $[100] = plotHeight;
      $[101] = plotWidth;
      $[102] = t45;
      $[103] = t46;
    } else {
      t46 = $[103];
    }
    if ($[104] !== t35 || $[105] !== t43 || $[106] !== t46) {
      t23 = <defs>{t35}{t43}{t46}</defs>;
      $[104] = t35;
      $[105] = t43;
      $[106] = t46;
      $[107] = t23;
    } else {
      t23 = $[107];
    }
    if ($[108] !== plotLeft || $[109] !== svgHeight) {
      t5 = <line x1={plotLeft} x2={plotLeft} y1={0} y2={svgHeight} className={styles.yAxisLineLeft} />;
      $[108] = plotLeft;
      $[109] = svgHeight;
      $[110] = t5;
    } else {
      t5 = $[110];
    }
    if ($[111] !== plotRight || $[112] !== svgHeight) {
      t6 = <line x1={plotRight} x2={plotRight} y1={0} y2={svgHeight} className={styles.yAxisLineRight} />;
      $[111] = plotRight;
      $[112] = svgHeight;
      $[113] = t6;
    } else {
      t6 = $[113];
    }
    if ($[114] !== airtimeReferenceY || $[115] !== plotLeft || $[116] !== plotRight || $[117] !== showAirtimeReferenceLine) {
      t7 = showAirtimeReferenceLine && airtimeReferenceY !== null ? <line x1={plotLeft} x2={plotRight} y1={airtimeReferenceY} y2={airtimeReferenceY} className={styles.referenceLineAirtime} /> : null;
      $[114] = airtimeReferenceY;
      $[115] = plotLeft;
      $[116] = plotRight;
      $[117] = showAirtimeReferenceLine;
      $[118] = t7;
    } else {
      t7 = $[118];
    }
    if ($[119] !== plotLeft || $[120] !== plotRight || $[121] !== shareReferenceY || $[122] !== showShareReferenceLine) {
      t8 = showShareReferenceLine && shareReferenceY !== null ? <line x1={plotLeft} x2={plotRight} y1={shareReferenceY} y2={shareReferenceY} className={styles.referenceLineShare} /> : null;
      $[119] = plotLeft;
      $[120] = plotRight;
      $[121] = shareReferenceY;
      $[122] = showShareReferenceLine;
      $[123] = t8;
    } else {
      t8 = $[123];
    }
    const t47 = `url(#${airtimeEdgeMaskId})`;
    let t48;
    if ($[124] !== isHourly || $[125] !== xScale) {
      t48 = point_1 => xScale(progressPointXValue(point_1, isHourly));
      $[124] = isHourly;
      $[125] = xScale;
      $[126] = t48;
    } else {
      t48 = $[126];
    }
    let t49;
    if ($[127] !== airtimeScale) {
      t49 = point_2 => airtimeScale(point_2.accumulatedAirtime);
      $[127] = airtimeScale;
      $[128] = t49;
    } else {
      t49 = $[128];
    }
    const t50 = `url(#${airtimeAreaVerticalId})`;
    let t51;
    if ($[129] !== airtimeScale || $[130] !== chartSeries || $[131] !== t48 || $[132] !== t49 || $[133] !== t50) {
      t51 = <AreaClosed data={chartSeries} x={t48} y={t49} yScale={airtimeScale} stroke="none" fill={t50} curve={curveMonotoneX} />;
      $[129] = airtimeScale;
      $[130] = chartSeries;
      $[131] = t48;
      $[132] = t49;
      $[133] = t50;
      $[134] = t51;
    } else {
      t51 = $[134];
    }
    let t52;
    if ($[135] !== isHourly || $[136] !== xScale) {
      t52 = point_3 => xScale(progressPointXValue(point_3, isHourly));
      $[135] = isHourly;
      $[136] = xScale;
      $[137] = t52;
    } else {
      t52 = $[137];
    }
    let t53;
    if ($[138] !== airtimeScale) {
      t53 = point_4 => airtimeScale(point_4.accumulatedAirtime);
      $[138] = airtimeScale;
      $[139] = t53;
    } else {
      t53 = $[139];
    }
    let t54;
    if ($[140] !== chartSeries || $[141] !== t52 || $[142] !== t53) {
      t54 = <LinePath data={chartSeries} x={t52} y={t53} stroke="#FF9F0F" strokeWidth={2} curve={curveMonotoneX} />;
      $[140] = chartSeries;
      $[141] = t52;
      $[142] = t53;
      $[143] = t54;
    } else {
      t54 = $[143];
    }
    let t55;
    if ($[144] !== isHourly || $[145] !== xScale) {
      t55 = point_5 => xScale(progressPointXValue(point_5, isHourly));
      $[144] = isHourly;
      $[145] = xScale;
      $[146] = t55;
    } else {
      t55 = $[146];
    }
    let t56;
    if ($[147] !== shareScale) {
      t56 = point_6 => shareScale(point_6.sharePct);
      $[147] = shareScale;
      $[148] = t56;
    } else {
      t56 = $[148];
    }
    let t57;
    if ($[149] !== chartSeries || $[150] !== t55 || $[151] !== t56) {
      t57 = <LinePath data={chartSeries} x={t55} y={t56} stroke="rgba(255,255,255,0.75)" strokeWidth={3} curve={curveMonotoneX} />;
      $[149] = chartSeries;
      $[150] = t55;
      $[151] = t56;
      $[152] = t57;
    } else {
      t57 = $[152];
    }
    if ($[153] !== t47 || $[154] !== t51 || $[155] !== t54 || $[156] !== t57) {
      t9 = <g mask={t47}>{t51}{t54}{t57}</g>;
      $[153] = t47;
      $[154] = t51;
      $[155] = t54;
      $[156] = t57;
      $[157] = t9;
    } else {
      t9 = $[157];
    }
    let t58;
    if ($[158] !== airtimeReferenceTick) {
      t58 = yAxisTickLabelProps("left", airtimeReferenceTick);
      $[158] = airtimeReferenceTick;
      $[159] = t58;
    } else {
      t58 = $[159];
    }
    if ($[160] !== airtimeScale || $[161] !== airtimeTicks || $[162] !== plotLeft || $[163] !== t58) {
      t10 = <AxisLeft left={plotLeft} scale={airtimeScale} tickValues={airtimeTicks} tickLength={0} hideAxisLine={true} hideTicks={true} tickFormat={_temp3} tickLabelProps={t58} />;
      $[160] = airtimeScale;
      $[161] = airtimeTicks;
      $[162] = plotLeft;
      $[163] = t58;
      $[164] = t10;
    } else {
      t10 = $[164];
    }
    if ($[165] !== airtimeReferenceLabel || $[166] !== airtimeReferenceY || $[167] !== plotLeft || $[168] !== showAirtimeReferenceLine) {
      t11 = showAirtimeReferenceLine && airtimeReferenceY !== null ? <text x={plotLeft - Y_AXIS_LABEL_INSET} y={airtimeReferenceY + 16} textAnchor="end" className={styles.yAxisTargetLabel}>{airtimeReferenceLabel}</text> : null;
      $[165] = airtimeReferenceLabel;
      $[166] = airtimeReferenceY;
      $[167] = plotLeft;
      $[168] = showAirtimeReferenceLine;
      $[169] = t11;
    } else {
      t11 = $[169];
    }
    let t59;
    if ($[170] !== shareReferenceTick) {
      t59 = yAxisTickLabelProps("right", shareReferenceTick);
      $[170] = shareReferenceTick;
      $[171] = t59;
    } else {
      t59 = $[171];
    }
    if ($[172] !== plotRight || $[173] !== shareScale || $[174] !== shareTicks || $[175] !== t59) {
      t12 = <AxisRight left={plotRight} scale={shareScale} tickValues={shareTicks} tickLength={0} hideAxisLine={true} hideTicks={true} tickFormat={_temp4} tickLabelProps={t59} />;
      $[172] = plotRight;
      $[173] = shareScale;
      $[174] = shareTicks;
      $[175] = t59;
      $[176] = t12;
    } else {
      t12 = $[176];
    }
    if ($[177] !== plotRight || $[178] !== shareReferenceLabel || $[179] !== shareReferenceY || $[180] !== showShareReferenceLine) {
      t13 = showShareReferenceLine && shareReferenceY !== null ? <text x={plotRight + Y_AXIS_LABEL_INSET} y={shareReferenceY + 16} textAnchor="start" className={styles.yAxisTargetLabelRight}>{shareReferenceLabel}</text> : null;
      $[177] = plotRight;
      $[178] = shareReferenceLabel;
      $[179] = shareReferenceY;
      $[180] = showShareReferenceLine;
      $[181] = t13;
    } else {
      t13 = $[181];
    }
    let t60;
    if ($[182] !== pickDay) {
      t60 = event_1 => {
        if (event_1.pointerType === "touch") {
          return;
        }
        pickDay(event_1);
      };
      $[182] = pickDay;
      $[183] = t60;
    } else {
      t60 = $[183];
    }
    if ($[184] !== pickDay || $[185] !== plotInnerWidth || $[186] !== plotLeft || $[187] !== svgHeight || $[188] !== t60) {
      t14 = <rect x={plotLeft} y={0} width={plotInnerWidth} height={svgHeight} fill="transparent" onPointerMove={t60} onPointerDown={pickDay} />;
      $[184] = pickDay;
      $[185] = plotInnerWidth;
      $[186] = plotLeft;
      $[187] = svgHeight;
      $[188] = t60;
      $[189] = t14;
    } else {
      t14 = $[189];
    }
    t15 = isHourly ? <g className={styles.dayLabelsGroup}>{HOURLY_AXIS_LABELS.map((hour_0, index) => {
        const labelIndex = index + 1;
        const isFirst = labelIndex === 1;
        const isLast = labelIndex === HOURLY_AXIS_LABELS.length;
        const isSelected = isInspecting && selectedDay === hour_0;
        return <text key={hour_0} x={hourlyLabelX(labelIndex, dayLabelLeft, dayLabelRight)} y={dayLabelY} textAnchor={getEdgeTextAnchor(isFirst, isLast)} dominantBaseline="text-after-edge" className={cn(styles.hourLabel, isSelected && styles.hourLabelSelected)}>{formatHourWithPeriod(hour_0)}</text>;
      })}</g> : <g className={styles.dayLabelsGroup}>{DAYS.map(day_0 => {
        const isSelected_0 = isInspecting && day_0 === boundedIndex;
        const isFirstDay = day_0 === 1;
        const isLastDay = day_0 === dayCount;
        return <text key={day_0} x={dayLabelX(day_0, dayCount, dayLabelLeft, dayLabelRight)} y={dayLabelY} textAnchor={getEdgeTextAnchor(isFirstDay, isLastDay)} dominantBaseline="text-after-edge" className={cn(styles.dayLabel, isSelected_0 && styles.dayLabelSelected)}>{day_0}</text>;
      })}</g>;
    t16 = isInspecting && showInspectionTooltip ? <><line x1={selectedX} x2={selectedX} y1={0} y2={plotHeight} stroke="var(--color-primitive-brand-dark)" strokeOpacity={0.9} strokeWidth={1} /><circle cx={selectedX} cy={selectedAirtimeY} r={8} fill="var(--color-primitive-brand-light)" /><circle cx={selectedX} cy={selectedShareY} r={8} fill="var(--color-primitive-grey-60)" stroke="var(--color-primitive-white-60)" strokeWidth={1} /><foreignObject x={tooltipLeft} y={tooltipTop} width={TOOLTIP_WIDTH} height={TOOLTIP_HEIGHT} pointerEvents="none" className={styles.inspectionTooltip}><GraphContextMessage date={formatTooltipDate(selectedPoint.date, isHourly ? boundedIndex : selectedPoint.hour)} className={styles.inspectionTooltip} airtime={formatTooltipAirtime(selectedPoint)} shareOfAirtime={formatSharePercent(selectedPoint.sharePct)} /></foreignObject></> : null;
    $[3] = airtimeAreaVerticalId;
    $[4] = airtimeEdgeMaskGradId;
    $[5] = airtimeEdgeMaskId;
    $[6] = airtimeMax;
    $[7] = airtimeReferenceLabel;
    $[8] = airtimeReferenceTick;
    $[9] = airtimeTicks;
    $[10] = dayCount;
    $[11] = dayLabelLeft;
    $[12] = dayLabelRight;
    $[13] = graphHeight;
    $[14] = isHourly;
    $[15] = onSelectDay;
    $[16] = plotHeight;
    $[17] = plotInnerWidth;
    $[18] = plotLeft;
    $[19] = plotRight;
    $[20] = plotWidth;
    $[21] = progressData;
    $[22] = referenceAirtimeHours;
    $[23] = referenceSharePct;
    $[24] = selectedDay;
    $[25] = shareMax;
    $[26] = shareReferenceLabel;
    $[27] = shareReferenceTick;
    $[28] = shareTicks;
    $[29] = showAirtimeReferenceLine;
    $[30] = showShareReferenceLine;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
    $[44] = t23;
    $[45] = t5;
    $[46] = t6;
    $[47] = t7;
    $[48] = t8;
    $[49] = t9;
  } else {
    t10 = $[31];
    t11 = $[32];
    t12 = $[33];
    t13 = $[34];
    t14 = $[35];
    t15 = $[36];
    t16 = $[37];
    t17 = $[38];
    t18 = $[39];
    t19 = $[40];
    t20 = $[41];
    t21 = $[42];
    t22 = $[43];
    t23 = $[44];
    t5 = $[45];
    t6 = $[46];
    t7 = $[47];
    t8 = $[48];
    t9 = $[49];
  }
  let t24;
  if ($[190] !== t10 || $[191] !== t11 || $[192] !== t12 || $[193] !== t13 || $[194] !== t14 || $[195] !== t15 || $[196] !== t16 || $[197] !== t5 || $[198] !== t6 || $[199] !== t7 || $[200] !== t8 || $[201] !== t9) {
    t24 = <g>{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}</g>;
    $[190] = t10;
    $[191] = t11;
    $[192] = t12;
    $[193] = t13;
    $[194] = t14;
    $[195] = t15;
    $[196] = t16;
    $[197] = t5;
    $[198] = t6;
    $[199] = t7;
    $[200] = t8;
    $[201] = t9;
    $[202] = t24;
  } else {
    t24 = $[202];
  }
  let t25;
  if ($[203] !== t17 || $[204] !== t18 || $[205] !== t19 || $[206] !== t20 || $[207] !== t21 || $[208] !== t22 || $[209] !== t23 || $[210] !== t24) {
    t25 = <svg width={t17} height={t18} viewBox={t19} className={t20} aria-label={t21} onPointerLeave={t22}>{t23}{t24}</svg>;
    $[203] = t17;
    $[204] = t18;
    $[205] = t19;
    $[206] = t20;
    $[207] = t21;
    $[208] = t22;
    $[209] = t23;
    $[210] = t24;
    $[211] = t25;
  } else {
    t25 = $[211];
  }
  return t25;
}
function _temp4(v_0) {
  return `${v_0}%`;
}
function _temp3(v) {
  return `${v}h`;
}
function _temp2(point) {
  return point.hasData !== false;
}
function _temp(_, idx) {
  return idx + 1;
}
export function OverviewProgressChart(t0) {
  const $ = _c(40);
  const {
    progressData,
    summaryData,
    isLoading: t1,
    startDate,
    className
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const isPositiveValue = _temp5;
  const airtimeReferenceHours = resolveAirtimeReferenceHours(progressData);
  const shareReferencePct = isPositiveValue(progressData?.sovTarget) ? Math.round(progressData?.sovTarget ?? 0) : null;
  const showAirtimeReferenceLine = airtimeReferenceHours !== null;
  const showShareReferenceLine = shareReferencePct !== null;
  const t2 = progressData?.airtimeTargetSource;
  let t3;
  if ($[0] !== t2) {
    t3 = referenceAxisLabel(t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const airtimeReferenceLabel = t3;
  const t4 = progressData?.sovTargetSource;
  let t5;
  if ($[2] !== t4) {
    t5 = referenceAxisLabel(t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const shareReferenceLabel = t5;
  const hourlyStats = summaryData?.hourlyStats;
  const hourlyGraphData = mapHourlyStatsToProgressPoints(hourlyStats ?? []);
  const useHourlyStats = hourlyGraphData.length > 0;
  const rawGraphData = useHourlyStats ? hourlyGraphData : mapDailyStatsToProgressPoints(summaryData?.dailyStats ?? []);
  const graphData = useHourlyStats ? rawGraphData : padProgressData(rawGraphData, startDate);
  let t6;
  bb0: {
    const labelDate = useHourlyStats ? rawGraphData[0]?.date ?? "" : startDate ?? "";
    if (!labelDate || !isValidStatDate(labelDate)) {
      t6 = "";
      break bb0;
    }
    const month = formatUtcDate(parseCalendarDate(labelDate), "MMMM");
    t6 = useHourlyStats ? `${month} (UTC)` : month;
  }
  const monthLabel = t6;
  const [selectedDay, setSelectedDay] = useState(null);
  const lastItemAirtime = rawGraphData.length > 0 ? rawGraphData[rawGraphData.length - 1]?.accumulatedAirtime ?? 0 : 0;
  const currentAirtimeHours = lastItemAirtime;
  const airtimeReferenceTick = airtimeReferenceHours !== null ? Math.round(airtimeReferenceHours) : 0;
  const shareReferenceTick = shareReferencePct !== null ? shareReferencePct : 0;
  const airtimeMax = computeAirtimeScaleMaxHours(currentAirtimeHours, airtimeReferenceHours);
  const airtimeTicks = uniqueDesc([airtimeMax, ...(airtimeReferenceHours !== null ? [airtimeReferenceTick] : []), 0]);
  const shareTicks = uniqueDesc([DEFAULT_SHARE_AXIS_MAX, ...(shareReferencePct !== null ? [shareReferenceTick] : []), 0]);
  const containerRef = useRef(null);
  const {
    width,
    height
  } = useContainerSize(containerRef);
  let t7;
  if ($[4] !== setSelectedDay) {
    t7 = event => {
      if (event.pointerType !== "touch") {
        return;
      }
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      setSelectedDay(null);
    };
    $[4] = setSelectedDay;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const dismissOnOutsideTap = t7;
  let t8;
  if ($[6] !== dismissOnOutsideTap || $[7] !== selectedDay) {
    t8 = () => {
      if (selectedDay === null) {
        return;
      }
      document.addEventListener("pointerdown", dismissOnOutsideTap);
      return () => document.removeEventListener("pointerdown", dismissOnOutsideTap);
    };
    $[6] = dismissOnOutsideTap;
    $[7] = selectedDay;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== selectedDay) {
    t9 = [selectedDay];
    $[9] = selectedDay;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  useEffect(t8, t9);
  let t10;
  if ($[11] !== className) {
    t10 = cn(styles.chartHost, className);
    $[11] = className;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== airtimeMax || $[14] !== airtimeReferenceHours || $[15] !== airtimeReferenceLabel || $[16] !== airtimeReferenceTick || $[17] !== airtimeTicks || $[18] !== graphData || $[19] !== height || $[20] !== isLoading || $[21] !== selectedDay || $[22] !== setSelectedDay || $[23] !== shareReferenceLabel || $[24] !== shareReferencePct || $[25] !== shareReferenceTick || $[26] !== shareTicks || $[27] !== showAirtimeReferenceLine || $[28] !== showShareReferenceLine || $[29] !== useHourlyStats || $[30] !== width) {
    t11 = !isLoading && width > 0 && height > 0 ? <ProgressChartSvg width={width} height={height} selectedDay={selectedDay} onSelectDay={setSelectedDay} referenceAirtimeHours={airtimeReferenceHours} airtimeReferenceTick={airtimeReferenceTick} showAirtimeReferenceLine={showAirtimeReferenceLine} airtimeReferenceLabel={airtimeReferenceLabel} referenceSharePct={shareReferencePct} shareReferenceTick={shareReferenceTick} showShareReferenceLine={showShareReferenceLine} shareReferenceLabel={shareReferenceLabel} airtimeMax={airtimeMax} shareMax={DEFAULT_SHARE_AXIS_MAX} airtimeTicks={airtimeTicks} shareTicks={shareTicks} progressData={graphData} isHourly={useHourlyStats} /> : null;
    $[13] = airtimeMax;
    $[14] = airtimeReferenceHours;
    $[15] = airtimeReferenceLabel;
    $[16] = airtimeReferenceTick;
    $[17] = airtimeTicks;
    $[18] = graphData;
    $[19] = height;
    $[20] = isLoading;
    $[21] = selectedDay;
    $[22] = setSelectedDay;
    $[23] = shareReferenceLabel;
    $[24] = shareReferencePct;
    $[25] = shareReferenceTick;
    $[26] = shareTicks;
    $[27] = showAirtimeReferenceLine;
    $[28] = showShareReferenceLine;
    $[29] = useHourlyStats;
    $[30] = width;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11) {
    t12 = <div ref={containerRef} className={styles.chartPlot}>{t11}</div>;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== monthLabel) {
    t13 = monthLabel ? <div className={styles.axisFooter}><span className={styles.axisFooterLeft}>{TARGET_CARD_MESSAGES.airtimeTitle}</span><span className={styles.axisFooterMonth}>{monthLabel}</span><span className={styles.axisFooterRight}>{TARGET_CARD_MESSAGES.shareOfAirtimeTitle}</span></div> : null;
    $[34] = monthLabel;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t10 || $[37] !== t12 || $[38] !== t13) {
    t14 = <div className={t10}>{t12}{t13}</div>;
    $[36] = t10;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  return t14;
}
function _temp5(value) {
  return value !== undefined && Number.isFinite(value) && value > 0;
}